/**
 * State Manager Service
 *
 * Central coordinator for all signal-based states.
 * Handles initialization, cleanup, and coordination of state modules.
 *
 * @example
 * // In app initialization
 * await stateManager.initializeStates(userContext);
 *
 * // On sign out
 * stateManager.resetAllStates();
 *
 * // On team switch
 * stateManager.resetTeamStates();
 */

import { Injectable, inject, effect } from '@angular/core';
import { AccountState } from './account';
import { AdminStudentsState } from './admin-students';
import { AdminUsersState } from './admin-users';
import { AdminGoalsState } from './admin-goals';
import { AdminObservationsState } from './admin-observations';
import { MyStudentsState } from './my-students';
import { MyGoalsState } from './my-goals';
import { MyObservationsState } from './my-observations';
import { StateCacheService } from 'src/app/core/services/state-cache.service';
import { MyWorkshopRefsState } from 'src/app/store/workshops';
import { GovernanceState } from './governance/governance.state';
// import { AdminState } from './admin';
// import { BlumeAdminState } from './blume-admin';
// import { WorkspaceState } from './workspace';

/**
 * User context for state initialization
 */
export interface UserContext {
  /** User's email address */
  email: string;

  /** Is the user a Blume admin? */
  isBlumeAdmin: boolean;

  /** Is the user a team admin? */
  isTeamAdmin: boolean;

  /** Currently active team ID */
  activeTeamId: string | null;

  /** Firebase Auth UID */
  uid: string;

  /**
   * All teams the user belongs to, each with their caseload student IDs.
   * Used to pre-load cross-team data for community home widgets.
   * The active team entry (if present) is handled by the standard init path;
   * additional entries trigger supplemental loads merged into the same state.
   */
  allTeamContexts?: Array<{ teamId: string; studentIds: string[] }>;
}

@Injectable({
  providedIn: 'root',
})
export class StateManager {
  // =============================================
  // STATE DEPENDENCIES
  // =============================================
  private readonly accountState = inject(AccountState);
  private readonly studentsState = inject(AdminStudentsState);
  private readonly usersState = inject(AdminUsersState);
  private readonly adminGoalsState = inject(AdminGoalsState);
  private readonly adminObservationsState = inject(AdminObservationsState);
  private readonly myStudentsState = inject(MyStudentsState);
  private readonly myGoalsState = inject(MyGoalsState);
  private readonly myObservationsState = inject(MyObservationsState);
  private readonly workshopsService = inject(MyWorkshopRefsState);
  private readonly governanceState = inject(GovernanceState);
  private readonly stateCacheService = inject(StateCacheService);
  // Uncomment as you create these states:
  // private readonly adminState = inject(AdminState);
  // private readonly blumeAdminState = inject(BlumeAdminState);
  // private readonly workspaceState = inject(WorkspaceState);

  // =============================================
  // INITIALIZATION TRACKING
  // =============================================
  private _initialized = false;

  constructor() {
    this.setupAutoResetListeners();
  }

  // =============================================
  // PUBLIC API
  // =============================================

  /**
   * Initialize all states based on user context
   *
   * Call this after authentication and user data is loaded.
   * This will initialize states in the correct order based on user permissions.
   *
   * @param context - User context with permissions and identifiers
   * @throws Error if any state initialization fails
   *
   * @example
   * const context: UserContext = {
   *   email: 'user@example.com',
   *   isBlumeAdmin: false,
   *   isTeamAdmin: true,
   *   activeTeamId: 'team-123',
   *   uid: 'abc123'
   * };
   * await stateManager.initializeStates(context);
   */
  async initializeStates(context: UserContext): Promise<void> {
    if (this._initialized) {
      console.warn('[StateManager] States already initialized');
      return;
    }

    try {
      // Note: AccountState auto-initializes via Firebase Auth listener
      // We just need to wait for it to be ready if needed

      // Step 0.5: Start the active account's V3 governance listener FIRST —
      // downstream surfaces read the derived access mode. Fire-and-forget
      // real-time listener; the state is indeterminate until the first
      // snapshot and fails closed on error (see GovernanceState).
      if (context.activeTeamId && context.email) {
        this.governanceState.loadForAccount(context.activeTeamId, context.email);
      }

      // Step 1: Initialize Blume Admin State (Only for Blume admins)
      if (context.isBlumeAdmin) {
        // await this.blumeAdminState.initialize();
      }

      // Step 2: Initialize Admin-only States (team admins AND Blume admins
      // with an active team). Blume admins always have full access to the
      // admin portal regardless of the `isInitialTeamAdmin` flag on their
      // partner profile — gating Step 2 on `isTeamAdmin` alone meant Blume
      // staff silently loaded zero admin goals/observations.
      const needsAdminStates = (context.isTeamAdmin || context.isBlumeAdmin) && !!context.activeTeamId;
      if (needsAdminStates) {
        await this.adminGoalsState.initialize({
          activeTeamId: context.activeTeamId!,
          enableRealtimeUpdates: true,
        });

        // Initialize measurements for all students who have goals.
        // Must run after adminGoalsState so goal studentIds are available.
        const goalStudentIds = [
          ...new Set(
            this.adminGoalsState
              .data()
              .map(g => g.studentId)
              .filter(Boolean),
          ),
        ];
        await this.adminObservationsState.initialize({
          activeTeamId: context.activeTeamId!,
          studentIds: goalStudentIds,
        });
      }

      // Step 3: Initialize Workspace State (For users with active team)
      if (context.activeTeamId) {
        // await this.workspaceState.initialize({
        //   teamId: context.activeTeamId,
        // });
      }

      // Step 4: Initialize Team Students State (For users with active team)
      if (context.activeTeamId) {
        await this.studentsState.initialize({
          activeTeamId: context.activeTeamId,
          enableRealtimeUpdates: true,
        });
      }

      // Step 4.5: Initialize Team Users State (For users with active team)
      if (context.activeTeamId) {
        await this.usersState.initialize({
          activeTeamId: context.activeTeamId,
          enableRealtimeUpdates: true,
        });
      }

      // Step 4.6: Load workshop references for the current user (non-admin path)
      if (context.activeTeamId && context.email) {
        this.workshopsService
          .loadWorkshopReferences(context.activeTeamId, context.email)
          .catch(err => console.warn('[StateManager] Workshop references failed to load:', err));
      }

      // Step 5: Initialize caseload-scoped states in parallel (require studentIds)
      const studentIds = this.accountState.studentIds();

      // Build additional-team contexts (all teams minus the active one)
      const additionalTeamContexts = (context.allTeamContexts ?? []).filter(
        ctx => ctx.teamId !== context.activeTeamId && ctx.studentIds.length > 0,
      );

      if (context.activeTeamId && studentIds.length > 0) {
        await Promise.all([
          this.myStudentsState.initialize({
            activeTeamId: context.activeTeamId,
            studentIds,
            enableRealtimeUpdates: true,
            additionalTeamContexts,
          }),
          this.myGoalsState.initialize({
            activeTeamId: context.activeTeamId,
            caseload: studentIds,
            enableRealtimeUpdates: true,
            additionalTeamContexts: additionalTeamContexts.map(ctx => ({
              teamId: ctx.teamId,
              caseload: ctx.studentIds,
            })),
          }),
          this.myObservationsState.initialize({
            activeTeamId: context.activeTeamId,
            studentIds,
            enableRealtimeUpdates: true,
            additionalTeamContexts,
          }),
        ]);
      } else {
      }

      // Step 6: Initialize Rosters State (For users with active team)
      // Note: Rosters state is optional and can be initialized on-demand
      // Uncomment to auto-initialize on team selection:
      // if (context.activeTeamId) {
      //   console.log('[StateManager] Initializing rosters state');
      //   await this.rostersState.initialize({
      //     activeTeamId: context.activeTeamId,
      //     pageSize: 25,
      //   });
      // }

      this._initialized = true;
    } catch (error) {
      console.error('[StateManager] ❌ State initialization failed:', error);

      // Reset in-memory signal state so the next initialization attempt starts
      // clean. Deliberately do NOT call clearAllStateCaches() here — the primary
      // data caches (students, users) may have loaded successfully before the
      // failure occurred, and wiping them forces a full Firestore reload on the
      // next session. clearAllStateCaches() is reserved for sign-out and team
      // switch (see resetAllStates / resetTeamStates).
      this._initialized = false;

      throw error;
    }
  }

  /**
   * Reset all states to defaults
   *
   * Note: AccountState resets automatically via Firebase Auth listener.
   * This method resets team-specific states and clears all caches.
   *
   * @example
   * stateManager.resetAllStates();
   */
  resetAllStates(): void {
    console.trace('[StateManager] ↑ resetAllStates() called from here');

    // Clear all state caches (important on sign out)
    this.stateCacheService.clearAllStateCaches();

    // Note: AccountState resets itself automatically on Firebase sign out
    // This includes all user data, partners, invites, and profiles
    this.studentsState.reset();
    this.usersState.reset();
    this.adminGoalsState.reset();
    this.adminObservationsState.reset();
    this.myStudentsState.reset();
    this.myGoalsState.reset();
    this.myObservationsState.reset();
    this.workshopsService.clearWorkshopReferences();
    this.governanceState.clear();
    // this.blumeAdminState.reset();
    // this.workspaceState.reset();

    this._initialized = false;
  }

  /**
   * Reset only team-specific states
   *
   * Call this when switching teams to clear team-specific data and caches.
   *
   * @example
   * await stateManager.resetTeamStates();
   */
  async resetTeamStates(): Promise<void> {
    console.trace('[StateManager] ↑ resetTeamStates() called from here');

    // Clear all state caches (important when switching teams)
    this.stateCacheService.clearAllStateCaches();

    // Clear active profile when switching teams
    await this.accountState.clearActiveProfile();
    this.studentsState.reset();
    this.usersState.reset();
    this.adminGoalsState.reset();
    this.adminObservationsState.reset();
    this.myStudentsState.reset();
    this.myGoalsState.reset();
    this.myObservationsState.reset();
    this.workshopsService.clearWorkshopReferences();
    this.governanceState.clear();
    // this.adminState.reset();
    // this.workspaceState.reset();

    this._initialized = false;
  }

  /**
   * Reinitialize team states for a new team
   *
   * Convenience method that resets team states and reinitializes with new team.
   *
   * @param teamId - New team ID
   * @param isTeamAdmin - Is user admin of new team?
   *
   * @example
   * await stateManager.switchTeam('team-456', true);
   */
  async switchTeam(teamId: string, isTeamAdmin: boolean): Promise<void> {
    try {
      // Reset current team states
      await this.resetTeamStates();

      // Re-resolve governance for the NEW active account immediately — the
      // reset above revoked the prior account's access mode; this replaces
      // it so stale full-V3 capability can never carry across a switch.
      const accountEmail = this.accountState.accountEmail();
      if (accountEmail) {
        this.governanceState.loadForAccount(teamId, accountEmail);
      }

      // Initialize admin-only states
      if (isTeamAdmin) {
        await this.adminGoalsState.initialize({
          activeTeamId: teamId,
          enableRealtimeUpdates: true,
        });

        const goalStudentIds = [
          ...new Set(
            this.adminGoalsState
              .data()
              .map(g => g.studentId)
              .filter(Boolean),
          ),
        ];
        await this.adminObservationsState.initialize({
          activeTeamId: teamId,
          studentIds: goalStudentIds,
        });
      }

      // Initialize team students state
      await this.studentsState.initialize({
        activeTeamId: teamId,
        enableRealtimeUpdates: true,
      });

      // Initialize team users state
      await this.usersState.initialize({
        activeTeamId: teamId,
        enableRealtimeUpdates: true,
      });

      // Initialize caseload-scoped states in parallel
      const studentIds = this.accountState.studentIds();
      if (studentIds.length > 0) {
        await Promise.all([
          this.myStudentsState.initialize({ activeTeamId: teamId, studentIds, enableRealtimeUpdates: true }),
          this.myGoalsState.initialize({ activeTeamId: teamId, caseload: studentIds, enableRealtimeUpdates: true }),
          this.myObservationsState.initialize({ activeTeamId: teamId, studentIds, enableRealtimeUpdates: true }),
        ]);
      }

      // Initialize rosters state (optional - can be done on-demand)
      // await this.rostersState.initialize({
      //   activeTeamId: teamId,
      //   pageSize: 25,
      // });
    } catch (error) {
      console.error('[StateManager] ❌ Team switch failed:', error);
      throw error;
    }
  }

  /**
   * Check if states are initialized
   */
  get isInitialized(): boolean {
    return this._initialized;
  }

  // =============================================
  // PRIVATE METHODS
  // =============================================

  /**
   * Set up automatic state reset listeners
   *
   * Listens to state changes and automatically resets states when needed.
   */
  private setupAutoResetListeners(): void {
    // Listen to account state initialization changes
    // If account state is reset (e.g., on sign out), reset team-specific states
    effect(() => {
      const accountInitialized = this.accountState.initialized();

      // Log every effect run so we can trace unexpected resets in dev

      // If account state goes from initialized to not initialized,
      // it means user signed out - reset team-specific states
      if (this._initialized && !accountInitialized) {
        console.warn(
          '[StateManager] ⚠️ Account state reset detected while states were initialized — resetting team states',
        );
        console.trace('[StateManager] ↑ resetTeamStates() triggered from here');
        this.resetTeamStates();
      }
    });

    // You can add more automatic listeners here
    // For example, listening to team changes in account state
    // and automatically resetting team states
  }
}
